import { api } from './api';

export interface MaintenanceLog {
  _id: string;
  asset: {
    _id: string;
    name: string;
    type: string;
    location: string;
  };
  description: string;
  type: 'Preventive' | 'Corrective' | 'Emergency' | 'Inspection' | 'Upgrade' | 'Scheduled' | 'Schedule';
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Pending' | 'In Progress' | 'Completed' | 'Overdue' | 'Scheduled' | 'operational  ' | 'non-operational';
  reportedBy: {
    _id: string;
    name: string;
    email: string;
  };
  startDate: string;
  endDate: string;
  estimatedDuration: number;
  actualDuration: number | null;
  cost: number;
  vendor?: {
    _id: string;
    name: string;
  };
  engineer?: {
    _id: string;
    name: string;
  };
  imageUrl?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  spareParts?: { part: string; quantity: number }[];
}

export interface CreateMaintenanceData {
  asset: string;
  description: string;
  type: 'Preventive' | 'Corrective' | 'Emergency';
  startDate: string;
  endDate: string;
  vendor: string;
  notes?: string;
}

export interface RaiseMaintenanceTicketData {
  asset: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
  photo: string;
}

export interface MaintenanceFilters {
  status?: string;
  type?: string;
  priority?: string;
  page?: number;
  limit?: number;
}

export interface MaintenanceMetrics {
  openMaintenanceCount: number;
  completedMaintenanceCount: number;
  pendingMaintenanceCount: number;
  inProgressMaintenanceCount: number;
  scheduledMaintenanceCount: number;
  overdueMaintenanceCount: number;
  operationalAssetsPercentage: number;
}

export interface MaintenanceDashboardData {
  logs: {
    result: MaintenanceLog[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  metrics: MaintenanceMetrics;
}

export interface MaintenanceSchedule {
  _id: string;
  name: string;
  slug: string;
  asset: string; // Asset ID
  description: string;
  frequency: string;
  nextDate: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface MaintenanceScheduleResponse {
  message: string;
  data: {
    result: MaintenanceSchedule[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  error: boolean;
}

export interface CompleteCorrectiveMaintenanceData {
  workOrderId: string;
  completionDate: string;
  technicianName: string;
  supervisorName: string;
  supervisorApproval: 'Approved' | 'Not Approved';
  supportingDocument?: string;
}

export interface MaintenanceLogAPI {
  _id: string;
  asset: string; // asset ID
  type: string;
  description: string;
  status: string;
  priority: string;
  reportedBy: string; // user ID
  isOpen: boolean;
  spareParts: { part: string; quantity: number }[];
  createdAt: string;
  updatedAt: string;
  endDate?: string;
  notes?: string;
  startDate?: string;
  schedule?: string;
  __v?: number;
}

export interface MaintenanceLogAPIResponse {
  message: string;
  data: {
    result: MaintenanceLogAPI[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  };
  error: boolean;
}

export interface ScheduleMaintenanceData {
  asset?: string; // Only for create
  name: string;
  description: string;
  frequency: string;
  nextDate: string;
  vendor: string;
}

export const maintenanceService = {
  // Get maintenance logs with filters
  getMaintenanceLogs: async (filters?: MaintenanceFilters) => {
  const queryParams = new URLSearchParams();

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, value.toString());
    }
  });

  const response = await api.get(`/maintenance/log?${queryParams.toString()}`);
  return response.data;
},

  // Get single maintenance log
  getMaintenanceLog: async (id: string) => {
    const response = await api.get(`/maintenance/log/${id}`);
    return response.data;
  },

  // Create maintenance log
  createMaintenanceLog: async (data: CreateMaintenanceData) => {
    const response = await api.post('/maintenance/log', data);
    return response.data;
  },

  // Raise maintenance ticket
  raiseMaintenanceTicket: async (data: RaiseMaintenanceTicketData) => {
    const response = await api.post('/maintenance/log/raise/ticket', data);
    return response.data;
  },

  // Delete maintenance log
  deleteMaintenanceLog: async (id: string) => {
    const response = await api.delete(`/maintenance/log/${id}`);
    return response.data;
  },

  // Get maintenance dashboard data
  getMaintenanceDashboardData: async () => {
    const response = await api.get('/maintenance/log/dashboard/data');
    return response.data;
  },

  // Get maintenance schedules
  getMaintenanceSchedules: async (filters: MaintenanceFilters = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/maintenance/schedule?${queryParams.toString()}`);
    return response.data;
  },

  // Start corrective maintenance
  startCorrectiveMaintenance: async (id: string, data: any) => {
    const response = await api.put(`/maintenance/log/${id}`, data);
    return response.data;
  },

  // Complete corrective maintenance
  completeCorrectiveMaintenance: async (id: string, data: CompleteCorrectiveMaintenanceData) => {
    const response = await api.put(`/maintenance/log/corrective/complete/${id}`, data);
    return response.data;
  },

  // Get open tickets
  getOpenTickets: async (): Promise<MaintenanceLogAPIResponse> => {
    const response = await api.get('/maintenance/log/get/open');
    return response.data;
  },

  // Get logs by status
  getLogsByStatus: async (status: string): Promise<MaintenanceLogAPIResponse> => {
    const response = await api.get(`/maintenance/log/filter/check-status?status=${encodeURIComponent(status)}`);
    return response.data;
  },

  // Create schedule
  createSchedule: async (data: ScheduleMaintenanceData) => {
    const response = await api.post('/maintenance/schedule', data);
    return response.data;
  },

  // Update schedule
  updateSchedule: async (id: string, data: Omit<ScheduleMaintenanceData, 'asset'>) => {
    const response = await api.put(`/maintenance/schedule/${id}`, data);
    return response.data;
  },

  // Delete schedule
  deleteSchedule: async (id: string) => {
    const response = await api.delete(`/maintenance/schedule/${id}`);
    return response.data;
  },
};